'use client'

import { BuilderBlocks } from '@builder.io/react'
import { CheckIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'

import { ActionLink } from '@src/components/ActionLink/ActionLink'
import { Button } from '@src/components/Button/Button'
import type { SKUSelectionRailItemProps } from '@src/components/SKUSelectionRail/SKUSelectionRailItem/SKUSelectionRailItem.props'
import { SKUSelectionRailItemBorder } from '@src/components/SKUSelectionRail/SKUSelectionRailItem/SKUSelectionRailItemBorder'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useSKUSelectionStore } from '@src/stores/SKUSelectionStore'
import { cn } from '@src/utils/tailwind'

const SelectButton = ({ sku, railSlug }: { sku?: string; railSlug?: string }) => {
  const group = useSKUSelectionStore((state) => state.groups.get(railSlug ?? ''))
  const isSelected = sku ? (group?.includes(sku) ?? false) : false
  const { t } = useLocale('common')

  if (!sku || !railSlug) return null

  return (
    <div className="mt-3 flex justify-center">
      <ActionLink
        className={cn({
          'w-full': !isSelected && t('select').length > 6,
          'sm:flex sm:justify-center': !isSelected,
        })}
        actionName="selectSKU"
        selectSKU={{
          sku,
        }}
      >
        {isSelected ? (
          <div className="mx-2 flex size-11 items-center justify-center rounded-full bg-bodi-green-deep-500">
            <CheckIcon className="size-5 stroke-[2.5px] text-bodi-white" />
          </div>
        ) : (
          <Button
            size="medium"
            variant="secondary"
            className={cn(
              'border-bodi-gray-light hover:border-bodi-black-text hover:bg-transparent',
              { 'w-full': t('select').length > 6 }
            )}
          >
            {t('select')}
          </Button>
        )}
      </ActionLink>
    </div>
  )
}

export const SKUSelectionRailItem = (props: SKUSelectionRailItemProps) => {
  const {
    imageAlt,
    imageUrl,
    blocks,
    builderBlock,
    sku,
    columnsDesktop,
    imageStyle,
    railSlug,
    analyticsModule,
  } = props

  return (
    <>
      <ActionLink
        analyticsModule={analyticsModule}
        className="relative w-full"
        actionName="selectSKU"
        selectSKU={{
          sku,
        }}
      >
        <div
          className={cn('relative aspect-square', {
            'aspect-square': imageStyle === 'square',
            'aspect-250/171': imageStyle === 'horizontal',
            'aspect-250/309': imageStyle === 'vertical',
          })}
        >
          <Image
            className={cn(
              'absolute inset-0 rounded-[13px] bg-bodi-gray-pearl object-cover lg:rounded-[30px]',
              { 'lg:rounded-[14px]': (columnsDesktop ?? 0) >= 5 }
            )}
            fill
            src={imageUrl ?? ''}
            alt={imageAlt ?? ''}
          />
          <SKUSelectionRailItemBorder
            columnsDesktop={columnsDesktop}
            sku={sku}
            railSlug={railSlug}
          />
        </div>
        <BuilderBlocks
          parentElementId={builderBlock?.id}
          blocks={blocks}
          dataPath={`component.options.RailPickerItem.blocks`}
        />
      </ActionLink>
      <SelectButton sku={sku} railSlug={railSlug} />
    </>
  )
}
